import {computed, Injectable, signal} from '@angular/core';
import {BehaviorSubject, Observable, Subject, takeUntil} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {EnvironmentService} from '../environment.service';
import {ITeamGroup, IUnitDto, SiteDetailsUrlParams, TeamPicture} from '../../common/models/interfaces';
import {UserInfoQuery} from 'src/app/common/elf-store/user-info/user-info.query';
import {AuthService} from 'src/app/auth/shared/services/auth.service';
import {NewUnitOwnerDto, TeamMembership} from 'src/app/types/team-management.type';
import {TeamManagementApiService} from './team-management-api.service';
import {TeamManager} from '../../common/elf-store/team/team.manager';
import {TeamTabStateEnum} from '../../modules/account-management/account/team-table/types';
import {
  CreateAccountlessSubunitData,
  DeleteExistingUnitMemberData,
  GetAccountMfaInfoData,
  InviteUnitMemberData,
  PopulateUnitWithOwnerData,
  RemoveUnitTeamPictureData,
  ResendInviteToUnitMemberData,
  SetAgencyMfaConfigurationData,
  TeamMemberDtoMemberRolesEnum,
  UnitDto,
  UpdateGroupDto,
  UpdateGroupMembersDto,
  UpdateGroupSitesDto,
  UpdateUnitInfoData,
  UpdateUnitNameData,
  UpdateUnitTeamMemberPermissionsDtoRolesEnum,
  UploadUnitTeamPictureData,
} from '../../../userway/sdk/SSOApiClient';
import {ApiClient} from './userway-api.service';
import {SettingsApiService} from './settings-api.service';

export interface Team {
  name?: string;
  teamImageUrl?: string;
  teamMembersCount?: number;
}

export const TeamMemberRoles = [
  {
    value: TeamMemberDtoMemberRolesEnum.SITE_MANAGER,
    label: 'Manage Sites',
    text: 'Add sites, manage add-ons and site settings.',
  },
  {
    value: TeamMemberDtoMemberRolesEnum.BILLING_MANAGER,
    label: 'Manage Billing',
    text: 'Add subscriptions and manage payment methods.',
  },
  {
    value: TeamMemberDtoMemberRolesEnum.TEAM_AND_UNIT_MANAGER,
    label: 'Manage Teams',
    text: 'Add users and manage team member permissions.',
  },
  {
    value: TeamMemberDtoMemberRolesEnum.SCANS_MANAGER,
    label: 'Manage Accessibility Monitor',
    text: 'Add websites for real-time accessibility scanning.',
  },
  {
    value: TeamMemberDtoMemberRolesEnum.PDFS_MANAGER,
    label: 'Manage PDF Remediation',
    text: 'Add PDF files for manual or automated remediation.',
  },
  {
    value: TeamMemberDtoMemberRolesEnum.IMAGE_ALTS_MANAGER,
    label: 'Manage Image Alts',
    text: 'Add image alts to images on the website.',
  },
];

@Injectable({
  providedIn: 'root',
})
export class TeamManagementService {
  currentTab = signal<TeamTabStateEnum>(TeamTabStateEnum.Users);
  abortGetTeamData$ = new Subject<void>();
  siteDetailsUrlParams = signal<SiteDetailsUrlParams>({
    ownerId: null,
    siteName: null,
  });
  unitId: string;
  resetImageClicked = signal(false);
  saveClicked = signal(false);
  shouldRemovePhoto = computed(() => {
    return this.resetImageClicked() && this.saveClicked();
  });

  mfaDisabled = signal<boolean | null>(null);

  public isAdmin: boolean = false;

  constructor(
    private env: EnvironmentService,
    private userInfoQuery: UserInfoQuery,
    private http: HttpClient,
    private authService: AuthService,
    private teamManageApi: TeamManagementApiService,
    private teamManager: TeamManager,
    private apiClient: ApiClient,
    private settingsApiService: SettingsApiService,
  ) {}

  resetSiteDetailsUrlParams() {
    this.siteDetailsUrlParams.set({
      ownerId: null,
      siteName: null,
    });
  }

  resetPhotoButtonsStates() {
    this.resetImageClicked.set(false);
    this.saveClicked.set(false);
  }

  getTeamData(id?: string, options: {writeToStore: boolean} = {writeToStore: true}): Observable<IUnitDto> {
    this.abortGetTeamData$.next();
    return this.getTargetLink(id).pipe(
      takeUntil(this.abortGetTeamData$),
      switchMap(targetLink => {
        return this.http.get(`${targetLink}`).pipe(
          map((response: {payload: IUnitDto}) => {
            response.payload.teamMembers.teamImageUrl = this.withHash(response.payload.teamMembers.teamImageUrl);
            if (options.writeToStore) {
              this.teamManager.setTeamData(response.payload);
            }

            return response.payload;
          }),
        );
      }),
    );
  }

  initUserMfaStatus() {
    this.getUserMfaStatus().subscribe({
      next: (res: GetAccountMfaInfoData) => {
        this.mfaDisabled.set(
          !(res.payload?.enabledVirtualMfa || res.payload?.enabledEmailMfa) && !this.authService.isLoginAs,
        );
      },
    });
  }

  getUserMfaStatus() {
    return this.settingsApiService.getMfa();
  }

  updateTeamData(data: Team) {
    this.teamManager.updateTeamData(data as IUnitDto);
  }

  loadActiveMembership() {
    return this.teamManageApi.getTeamsMembership().pipe(
      tap((response: TeamMembership[]) => {
        response = response.map(teamMembership => ({
          ...teamMembership,
          myMembershipTeamOwnerPictureUrl: this.withHash(teamMembership.myMembershipTeamOwnerPictureUrl),
        }));
        this.teamManager.setUserMemberships(response);
      }),
    );
  }

  sendInvitation(data: any, unitId?: string): Observable<InviteUnitMemberData> {
    return this.getTargetId(unitId).pipe(
      switchMap(targetId => {
        return this.teamManageApi.sendInvitation(targetId, data);
      }),
    );
  }

  changeUserPermissions(
    data: {memberId?: string; roles: UpdateUnitTeamMemberPermissionsDtoRolesEnum[]},
    unitId?: string,
  ): Observable<any> {
    return this.getTargetId(unitId).pipe(
      switchMap(targetLink => {
        return this.teamManageApi.changeUserPermissions(targetLink, data);
      }),
    );
  }

  updateTeamName(data: {newTeamName: string}): Observable<any> {
    return this.getTargetId().pipe(
      switchMap(targetId => {
        return this.teamManageApi.updateTeamName(targetId, data).pipe(
          map((res: UpdateUnitNameData) => {
            this.updateTeamData({
              name: data.newTeamName,
            });
            return res;
          }),
        );
      }),
    );
  }

  resendInvitation(data: any, unitId?: string): Observable<ResendInviteToUnitMemberData> {
    return this.getTargetId(unitId).pipe(
      switchMap(targetId => {
        return this.teamManageApi.resendInvitation(targetId, data);
      }),
    );
  }

  removeUser(id: string, unitId?: string): Observable<DeleteExistingUnitMemberData> {
    return this.getTargetId(unitId).pipe(
      switchMap(targetId => {
        return this.teamManageApi.removeUser(targetId, id);
      }),
    );
  }

  updateTeamMember(memberId: string, data: any, unitId?: string): Observable<any> {
    return this.getTargetLink(unitId).pipe(
      switchMap(targetLink => {
        return this.teamManageApi.updateTeamMember({targetLink, memberId, data});
      }),
    );
  }

  enableTwoFactor(mfaRequired: boolean): Observable<SetAgencyMfaConfigurationData> {
    return this.getTargetId().pipe(
      switchMap(targetId => {
        return this.teamManageApi.enableTwoFactor(targetId, {mfaRequired: mfaRequired});
      }),
    );
  }

  uploadUserPicture(data: TeamPicture): Observable<UploadUnitTeamPictureData> {
    return this.getTargetId().pipe(
      switchMap(targetId => {
        return this.teamManageApi.uploadUserPicture(targetId, data).pipe(
          map((response: any) => {
            this.updateTeamData({
              teamImageUrl: this.withHash(response.payload.profilePictureImageUrl),
            });

            return response;
          }),
        );
      }),
    );
  }

  removeUserPicture(): Observable<RemoveUnitTeamPictureData> {
    return this.getTargetId().pipe(
      switchMap(targetLink => {
        this.updateTeamData({teamImageUrl: ''});
        return this.teamManageApi.removeUserPicture(targetLink);
      }),
    );
  }

  withHash(url: string): string {
    return url ? `${url}?hash=${Date.now()}` : '';
  }

  clearTeamData() {
    this.teamManager.reset();
  }

  createSubAccount(data: {name: string}, targetId?: string): Observable<CreateAccountlessSubunitData> {
    return this.getTargetId(targetId).pipe(
      switchMap(id => {
        return this.teamManageApi.createSubAccount(id, data);
      }),
    );
  }

  createNewGroup(data: ITeamGroup) {
    return this.getGroupTargetId().pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.createManageDashboardGroup(id, data);
        return this.apiClient.fetch(request);
      }),
    );
  }

  getTeamGroups(targetId?: string) {
    return this.getGroupTargetId(targetId).pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.getManageDashboardGroupsOfAccount(id);
        return this.apiClient.fetch(request);
      }),
      map(data => {
        this.teamManager.setGroups(data.payload.groups);
        return data.payload;
      }),
    );
  }

  updateTeamGroup(groupId: string, data: UpdateGroupDto) {
    return this.getGroupTargetId().pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.updateManageDashboardGroupGeneralInfo(
          id,
          groupId,
          data,
        );
        return this.apiClient.fetch(request);
      }),
    );
  }

  updateTeamGroupMembers(groupId: string, data: UpdateGroupMembersDto) {
    return this.getGroupTargetId().pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.updateMembersOfManageDashboardGroup(
          id,
          groupId,
          data,
        );
        return this.apiClient.fetch(request);
      }),
    );
  }

  updateTeamGroupSites(groupId: string, data: UpdateGroupSitesDto) {
    return this.getGroupTargetId().pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.updateSitesOfManageDashboardGroup(
          id,
          groupId,
          data,
        );
        return this.apiClient.fetch(request);
      }),
    );
  }

  deleteTeamGroup(groupId: string) {
    return this.getGroupTargetId().pipe(
      switchMap(id => {
        const request = this.apiClient.ssoApi.manageGroupController.deleteGroup(id, groupId);
        return this.apiClient.fetch(request);
      }),
    );
  }

  //Sub account units request
  getUnitInfo(unitId: string): Observable<UnitDto> {
    return this.getTargetLink(unitId).pipe(
      switchMap(link => {
        return this.http.get<{payload: UnitDto}>(link);
      }),
      map((data: {payload: UnitDto}) => data.payload),
    );
  }

  getUserUnits(targetId?: string) {
    return this.getTargetId(targetId).pipe(
      switchMap(id => {
        return this.teamManageApi.getUserUnits(id);
      }),
      map((response: {payload: {units: UnitDto[]}}) => response.payload.units),
      tap(units => this.teamManager.setTeamUnits(units)),
    );
  }

  addOwnerToUnit(
    unitIdentifier: string,
    data: NewUnitOwnerDto,
    targetId?: string,
  ): Observable<PopulateUnitWithOwnerData> {
    return this.getTargetId(targetId).pipe(
      switchMap(id => {
        return this.teamManageApi.addOwnerToUnit(id, unitIdentifier, data);
      }),
    );
  }

  updateSubUnitInfo(unitIdentifier: string, data: {newName?: string}): Observable<UpdateUnitInfoData> {
    return this.getTargetId().pipe(
      switchMap(id => {
        return this.teamManageApi.updateSubUnitInfo(id, unitIdentifier, data);
      }),
    );
  }

  removeSiteForAllGroups(siteId: number) {
    return this.getGroupTargetId().pipe(
      switchMap(groupId => {
        const request = this.apiClient.ssoApi.manageGroupController.removeSitesFromAllAccountGroups(groupId, {
          siteIdsToRemove: [siteId],
        });
        return this.apiClient.fetch(request);
      }),
    );
  }

  private getTargetLink(unitId?: string) {
    return this.userInfoQuery.userInfo$
      .pipe(filter(userInfo => !!userInfo.ssoUserId || !!userInfo.id))
      .pipe(take(1))
      .pipe(
        map(userInfo => {
          const targetId = unitId || this.authService.uWTargetSwitchId || userInfo.ssoUserId || userInfo.id;
          this.unitId = targetId;
          return `${this.env.apiUrl.apiUserSSO}unit/${targetId}`;
        }),
      );
  }

  private getTargetId(unitId?: string) {
    return this.userInfoQuery.userInfo$
      .pipe(filter(userInfo => !!userInfo.ssoUserId || !!userInfo.id))
      .pipe(take(1))
      .pipe(
        map(userInfo => {
          const targetId = unitId || this.authService.uWTargetSwitchId || userInfo.ssoUserId || userInfo.id;
          this.unitId = targetId;
          return targetId;
        }),
      );
  }

  private getGroupTargetId(targetId?: string) {
    return this.userInfoQuery.userInfo$
      .pipe(filter(userInfo => !!userInfo.ssoUserId || !!userInfo.accountCode))
      .pipe(take(1))
      .pipe(
        map(userInfo => {
          return targetId || userInfo.ssoUserId || userInfo.accountCode;
        }),
      );
  }
}
